// Realtime snapshot catch-up routes. All are reached via the generic catch-up
// dispatcher in `packages/core/src/stores/realtime/snapshot-store.ts`, which
// builds `/api/realtime/${kind}/catchup` from a runtime topic-kind — so no
// direct typed-client key references them. Tagged `caller: 'realtime'` to keep
// them out of the dead-route report (see CallerKind).
//
// GATED (BL-1833 PR 3a, ADR 062 — Access Grants). Every catch-up names its
// topic's record in the query, so the gate loads it and answers 404 for a
// missing, foreign or (unless `withDeleted`) archived one — the same answer for
// all three, so a foreign id's existence no longer shows in the status code.
import { responseType } from '@auctogon/core/api/registry';
import type { AdminBidLedgerCatchupResult } from '@auctogon/core/realtime/payloads/admin-bid-ledger';
import type { LotStateCatchupResult } from '@auctogon/core/realtime/payloads/admin-lot-state';
import type { MaxBidStateCatchupResult } from '@auctogon/core/realtime/payloads/admin-max-bids';
// Type-only (erased at build) — keeps the registry client-bundle-safe.
import type { StaffPrivateTopicsDto } from '../../schemas/responses/staff-private-topics';

import { defineAdminRoute } from './define-admin-route';
import { AUCTION_LOT_INFO_READ, LOT_EDIT_MODAL_READ } from './lots';

/** BL-2491 — the caller's own count at a house they belong to; no grant applies. */
const SELF_UNREADS = { none: "self — the caller's own unread @Inbox count, keyed on their session" } as const;

export const realtime = {
  // BL-2491 (ADR 056 — Delivery Record § 9(d)) — mints the signed-in staff
  // member's private topics at one house from the session and the gated tenant,
  // never from caller input. The typed client calls it, so it carries its DTO.
  staff_topics: defineAdminRoute({
    path: '/api/realtime/staff-topics',
    params: {},
    methods: {
      GET: {
        grant: SELF_UNREADS,
        tenant: { from: 'input', field: 'tenantId', in: 'query' },
        response: responseType<StaffPrivateTopicsDto>(),
      },
    },
  }),
  // BL-2491 — the staff unread-count snapshot's catch-up (ADR 011 § 4). The
  // generic dispatcher passes the house as `tenant_id` (parseSnapshotTopic's
  // staff branch); the route's `authorize` recomputes the row key from the
  // session and the gated house, and refuses any other key.
  staff_user_unreads_snapshot: {
    catchup: defineAdminRoute({
      path: '/api/realtime/staff_user_unreads_snapshot/catchup',
      params: {},
      methods: { GET: { grant: SELF_UNREADS, tenant: { from: 'input', field: 'tenant_id', in: 'query' } } },
      caller: 'realtime',
    }),
  },
  // `withDeleted`: an own-tenant archived auction is served as a tombstone
  // (BUG-1686) — the archived-auction view subscribes to it.
  // BL-2349: + inventory.read — the Lot Info tab's header (and an auction
  // lot's drawer) subscribe here; the projection carries no bidder data.
  auction_snapshot: {
    catchup: defineAdminRoute({
      path: '/api/realtime/auction_snapshot/catchup',
      params: {},
      methods: {
        GET: {
          grant: AUCTION_LOT_INFO_READ,
          tenant: { from: 'record', table: 'auction', param: 'topic_id', in: 'query', withDeleted: true },
        },
      },
      caller: 'realtime',
    }),
  },
  // ADR 042 / BL-1251 — admin lot-state stream catch-up. Reached via the
  // RealtimeLotsProvider's fetcher, which reads through the typed client since
  // BL-2009, so both scopes carry their DTO (ADR 027 — Response Contract): every
  // money field is a MoneyWire.
  //
  // Two scopes, two routes (BL-1833 PR 3a): the clerk page's WHOLE-AUCTION
  // form stays here (`lot.read` = auctions.view: Admin and, since BL-2283,
  // Accounting); the Bids tab's
  // ONE-LOT form moved to `lot/catchup`, which admits every role that can open
  // the lot (BL-1876). One declaration names one record, and the two forms key
  // on different tables. Both mint the auction's topic — in-tenant reads are
  // not sensitive for any staff role (Michael Lawrie, 2026-09-24).
  admin_lot_state: {
    catchup: defineAdminRoute({
      path: '/api/realtime/admin_lot_state/catchup',
      params: {},
      methods: {
        GET: {
          grant: 'lot.read',
          tenant: { from: 'record', table: 'auction', param: 'auction_id', in: 'query', withDeleted: true },
          response: responseType<LotStateCatchupResult>(),
        },
      },
      caller: 'realtime',
    }),
    // `withDeleted`: a deleted lot's removal must still reach an open Bids tab
    // (BUG-1513 deletion propagation).
    lot: {
      catchup: defineAdminRoute({
        path: '/api/realtime/admin_lot_state/lot/catchup',
        params: {},
        methods: {
          GET: {
            grant: LOT_EDIT_MODAL_READ,
            tenant: { from: 'record', table: 'lot', param: 'lot_id', in: 'query', withDeleted: true },
            response: responseType<LotStateCatchupResult>(),
          },
        },
        caller: 'realtime',
      }),
    },
  },
  // BUG-1467 — the PER-LOT admin de-anon bid-ledger catch-up, the clerk
  // console's bid record source. Reached via `fetchAdminLotBidLedgerCatchup`
  // (raw fetch, `caller: 'realtime'`), same as the catch-ups above.
  // Uncapped by design: the retired auction-wide feed's 2,000-bid cold-seed cap is what
  // made the pane show "No bids on this lot" on a lot that had sold.
  // Admits every role that opens the lot's Bids tab (BL-1876).
  // BL-2018: the one catch-up here the fetcher reads through the typed client,
  // so it carries its DTO (ADR 027 — Response Contract) instead of a cast.
  admin_lot_bid_ledger: {
    catchup: defineAdminRoute({
      path: '/api/realtime/admin_lot_bid_ledger/catchup',
      params: {},
      methods: {
        GET: {
          grant: LOT_EDIT_MODAL_READ,
          tenant: { from: 'record', table: 'lot', param: 'lot_id', in: 'query' },
          response: responseType<AdminBidLedgerCatchupResult>(),
        },
      },
      caller: 'realtime',
    }),
  },
  // BL-1262 — admin auction-registrations catch-up. Reached via
  // `createAdminRegistrationsCatchupFetcher` (raw fetch, `caller: 'realtime'`),
  // not the typed client, same as the catch-up routes above. NOTE this is the
  // AUCTION-scoped admin stream that mints the obscured channel — distinct from
  // the customer-keyed bidder route named in the BUG-791 note below.
  admin_auction_registrations: {
    catchup: defineAdminRoute({
      path: '/api/realtime/admin_auction_registrations/catchup',
      params: {},
      methods: {
        GET: {
          grant: 'auction.read',
          tenant: { from: 'record', table: 'auction', param: 'auction_id', in: 'query', withDeleted: true },
        },
      },
      caller: 'realtime',
    }),
  },
  // BL-1489 — admin TENANT-scoped conversations catch-up (the clerk's shared
  // inbox TRIGGER). Reached via `createAdminConversationsCatchupFetcher` (raw
  // fetch, `caller: 'realtime'`). Identifiers only; clerking.manage, via the
  // clerk bundle's `lot.hammer` (see `conversations.ts`).
  admin_conversations: {
    catchup: defineAdminRoute({
      path: '/api/realtime/admin_conversations/catchup',
      params: {},
      methods: { GET: { grant: 'lot.hammer', tenant: { from: 'input', field: 'tenant_id', in: 'query' } } },
      caller: 'realtime',
    }),
  },
  // BL-1552 — admin PER-LOT max-bid catch-up (the clerk console's pre-bid
  // section and `max $X` chips). Reached via
  // `createAdminLotMaxBidsCatchupFetcher`, which reads through the typed client
  // (BL-2010), so the entry carries its DTO (ADR 027 — Response Contract) and
  // the MoneyWire ceilings are never cast at the fetch. CONFIDENTIAL: this
  // stream carries `customer_id` + the bidder's stored ceiling, so it rides the
  // obscured admin channel only.
  //
  // Replaced the auction-wide `admin_max_bids` entry, whose route returned every
  // ceiling in the sale (5.7 MB at 2,500 lots) to serve one selected lot. Deleted,
  // not deprecated (ADR 044 — Must Consolidate).
  // Staff may see max-bid ceilings on the Bids tab (BL-1876); bidders never do.
  admin_lot_max_bids: {
    catchup: defineAdminRoute({
      path: '/api/realtime/admin_lot_max_bids/catchup',
      params: {},
      methods: {
        GET: {
          grant: LOT_EDIT_MODAL_READ,
          tenant: { from: 'record', table: 'lot', param: 'lot_id', in: 'query' },
          response: responseType<MaxBidStateCatchupResult>(),
        },
      },
      caller: 'realtime',
    }),
  },
  // BUG-791: the admin `user_registrations_snapshot/catchup` route was
  // removed — admin-web is the publisher side and has no registrations
  // snapshot subscriber. The customer-keyed bidder catch-up route
  // (`/api/realtime/customer_registrations_snapshot/catchup`) is the only
  // surviving registrations catch-up surface.
};
